/* eslint-disable react-refresh/only-export-components -- standard context pattern: provider, hook, and context co-located */
import type { ReactNode } from 'react';
import { createContext, useContext } from 'react';

import type { LinkedInDeliveryState } from './deliveryTypes';
import { useLinkedInDeliveryStates } from './useLinkedInDeliveryStates';

type DeliveryStatesByActionId = Record<number, LinkedInDeliveryState>;

const EMPTY_DELIVERY_STATES: DeliveryStatesByActionId = {};

const DeliveryStatesContext = createContext<DeliveryStatesByActionId>(
  EMPTY_DELIVERY_STATES
);

interface DeliveryStatesProviderProps {
  orgId: number;
  actionIds: number[];
  /**
   * True once the proposal is approved/executing, so an outbound row is
   * imminent. Drives the delivery hook's bootstrap poll so a freshly-queued row
   * is discovered without a manual refresh.
   */
  expectsDeliveryRows: boolean;
  children: ReactNode;
}

/**
 * Provides LinkedIn outbound delivery states, keyed by proposed_action_id,
 * to any descendant that needs to render a delivery badge. Wraps
 * {@link useLinkedInDeliveryStates}, which refreshes on an interval while any
 * state is non-terminal so a late Failed/Expired becomes visible, and
 * bootstrap-polls after approval so a newly-queued row appears promptly.
 */
export function DeliveryStatesProvider({
  orgId,
  actionIds,
  expectsDeliveryRows,
  children,
}: DeliveryStatesProviderProps) {
  const states = useLinkedInDeliveryStates(
    orgId,
    actionIds,
    expectsDeliveryRows
  );
  return (
    <DeliveryStatesContext.Provider value={states}>
      {children}
    </DeliveryStatesContext.Provider>
  );
}

/**
 * Reads the LinkedIn delivery state for a single proposed action id from
 * context. An unwrapped consumer receives the empty default and this returns
 * `undefined` rather than throwing.
 */
export function useDeliveryState(
  actionId?: number
): LinkedInDeliveryState | undefined {
  const states = useContext(DeliveryStatesContext);
  return actionId != null ? states[actionId] : undefined;
}
